import {
  ColumnDefinition,
  CellType,
  RangeType,
  TimeInterval,
  Timezone,
} from '@/features/report-snapshots/api/schema';
import { formatSeconds } from '@/features/report-snapshots/formatters/duration';
import { useFormatters } from '@/features/report-snapshots/formatters/use-formatters';

function formatBucketRange(range: RangeType) {
  if (range.to === null) {
    return `> ${formatSeconds(range.from)}`;
  }
  if (range.from === 0) {
    return `< ${formatSeconds(range.to)}`;
  }

  return `${formatSeconds(range.from)} - ${formatSeconds(range.to)}`;
}

export function formatCellValue(
  columnDefinition: ColumnDefinition,
  formatters: ReturnType<typeof useFormatters>,
  timezone: Timezone,
  cellValue: CellType,
  interval: TimeInterval,
  isChart?: boolean,
) {
  if (cellValue === null) {
    return '-';
  }

  const { unit, fieldType } = columnDefinition;

  switch (unit) {
    case 'timestamp':
      return formatters.timestamp(interval, cellValue as number, timezone);
    case 'percent':
    case 'ratio': {
      const value = cellValue as { numerator: number; denominator: number };
      const fractionUnit = columnDefinition.fractionUnit ?? {
        numerator: 'value',
        denominator: 'value',
      };
      const fraction = {
        numerator: { value: value.numerator, unit: fractionUnit.numerator },
        denominator: { value: value.denominator, unit: fractionUnit.denominator },
      };
      const numerator = formatters.valueFormatters(value.numerator, fractionUnit.numerator);
      const denominator = formatters.valueFormatters(value.denominator, fractionUnit.denominator);
      const division = formatters.valueFormatters(fraction, unit);
      if (division === '\u2014') {
        return division;
      }
      return isChart ? division : `${division} (${numerator}/${denominator})`;
    }
    case 'seconds':
      if (fieldType === 'range') {
        return formatBucketRange(cellValue as RangeType);
      } else {
        return formatSeconds(cellValue as number | null);
      }
    case 'value':
      return formatters.valueFormatters(roundValue(cellValue as number), unit);
    default:
      return cellValue;
  }
}

export function formatComparisonValue(
  comparisonValue: number,
  columnDefinition: ColumnDefinition,
  formatters: ReturnType<typeof useFormatters>,
) {
  if (!Number.isFinite(comparisonValue)) {
    return '\u2014';
  }

  const { unit } = columnDefinition;

  switch (unit) {
    case 'percent':
      return (comparisonValue * 100).toFixed(1) + '%';
    case 'ratio':
      return comparisonValue.toFixed(1);
    case 'seconds':
      return formatSeconds(Math.abs(comparisonValue));
    default: {
      return formatters.valueFormatters(roundValue(comparisonValue), unit);
    }
  }
}

function roundValue(value: number) {
  const shouldShowDecimals = value % 1 !== 0;
  if (shouldShowDecimals) {
    const roundedValue = Number(value.toFixed(1));
    return roundedValue;
  } else {
    return value;
  }
}
